import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, ExternalLink } from "lucide-react";
import { verifyLicense, certificateUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";

const verifyTexture = "https://images.unsplash.com/photo-1765046255478-55efc763637c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjB3aGl0ZSUyMGRpZ2l0YWwlMjBkYXRhJTIwZ3JpZCUyMHN0cnVjdHVyZXxlbnwwfHx8fDE3Nzc1MzQxODd8MA&ixlib=rb-4.1.0&q=85";

export default function Verify() {
  const { lid } = useParams();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    verifyLicense(lid).then(setData).catch((e) => setErr(e.message));
  }, [lid]);

  if (err) return <div className="max-w-3xl mx-auto p-12">{err}</div>;
  if (!data) return <div className="max-w-3xl mx-auto p-12 label-mono">{t("verify.verifying")}</div>;

  const valid = data.valid;
  const langSuffix = lang === "ar" ? "?lang=ar" : "";
  return (
    <div className="relative" data-testid="verify-page">
      <div className="absolute inset-0 -z-10 opacity-15">
        <img src={verifyTexture} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/70" />
      </div>
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="label-mono">{t("verify.heading_label")}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">{t("verify.heading")}</h1>

        <div className={`mt-10 paper p-8 grid gap-5 border-2 ${valid ? "bdr-ds" : "bdr-cs"}`} data-testid="verify-card">
          <div className="flex items-center gap-3">
            {valid ? (
              <span className="inline-flex items-center gap-2 px-3 py-1 tint-ds border label-mono">
                <ShieldCheck size={14} /> {t("verify.valid")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-3 py-1 tint-cs border label-mono">
                <ShieldX size={14} /> {data.license_id ? t("verify.invalid") : t("verify.not_found")}
              </span>
            )}
            <code className="font-mono-disp" data-testid="verify-license-id">{data.license_id}</code>
          </div>

          {data.candidate_name && (
            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-[var(--iddl-rule)]">
              <Field label={t("verify.f_candidate")} value={data.candidate_name} />
              <Field label={t("verify.f_outcome")} value={data.overall_outcome} />
              <Field label={t("verify.f_issued")} value={fmt(data.issued_at)} />
              <Field label={t("verify.f_expires")} value={fmt(data.expires_at)} />
            </div>
          )}

          {valid && (
            <a
              href={`${certificateUrl(data.license_id)}${langSuffix}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 label-mono hover:text-[var(--iddl-ink)]"
              data-testid="verify-pdf-link"
            >
              {t("verify.download_pdf")} <ExternalLink size={12} />
            </a>
          )}
        </div>

        <p className="text-xs text-[var(--iddl-muted)] mt-8 max-w-xl leading-relaxed">
          {t("verify.footnote")}
        </p>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className="font-display text-base mt-1">{value || "—"}</div>
    </div>
  );
}
function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toUTCString().slice(5, 16); } catch { return iso; }
}
